import { formatQtyWithUnit, trimTrailingZeros } from '../format.js';
import { html } from '../html.js';
import { t } from '../strings.js';
import { ltr, money } from './parts.js';
/**
 * The parts wave 2's eight record documents share, so that each template is
 * the handful of header fields that make it THAT document and nothing else.
 *
 * The line table and the totals block are the sales invoice's, lifted out
 * rather than copied: INV-H6's print options (`showItemCode`,
 * `showDiscountColumn`, `showTaxColumn`) are company-wide decisions, and a
 * company that hides the discount column on its invoices has hidden it on its
 * quotations too — one renderer is what makes that true without a test per
 * kind. The invoice template keeps its own table only because it also carries
 * INV-L3's per-line warehouse column, which no other kind has.
 */
/** Every status wave 2's kinds can be in, mapped to the word the paper prints. */
const STATUS_KEYS = {
    draft: 'statusDraft',
    cancelled: 'statusCancelled',
    sent: 'statusSent',
    accepted: 'statusAccepted',
    rejected: 'statusRejected',
    expired: 'statusExpired',
    converted: 'statusConverted',
    confirmed: 'statusConfirmed',
    partially_delivered: 'statusPartiallyDelivered',
    fulfilled: 'statusFulfilled',
    closed: 'statusClosed',
    approved: 'statusApproved',
    partially_received: 'statusPartiallyReceived',
    received: 'statusReceived',
    posted: 'statusPosted',
    credited: 'statusCredited',
    refunded: 'statusRefunded',
};
export function statusLabel(status, locale) {
    return t(STATUS_KEYS[status], locale);
}
/**
 * The invoice's rule, applied to every kind: a draft is not a document, and
 * it prints WITH THE WORD DRAFT ON IT. Same for a cancelled one. Every other
 * state is a header field, not a stamp — "confirmed" on an order is normal.
 */
export function statusBadge(status, locale) {
    if (status !== 'draft' && status !== 'cancelled')
        return null;
    return html `<span class="doc-badge">${statusLabel(status, locale)}</span>`;
}
const REASON_KEYS = {
    damaged_goods: 'reasonDamagedGoods',
    wrong_item_shipped: 'reasonWrongItemShipped',
    customer_changed_mind: 'reasonCustomerChangedMind',
    overstock: 'reasonOverstock',
    other: 'reasonOther',
};
export function reasonLabel(reason, locale) {
    return t(REASON_KEYS[reason], locale);
}
export function yesNo(value, locale) {
    return t(value ? 'yes' : 'no', locale);
}
/**
 * N-4 / N-14: the decimals the item's unit allows and the unit beside the
 * figure, inside ONE ltr() island so the pair does not come apart in the
 * Arabic document. A line with no item has no unit and prints as written.
 */
export function qtyCell(qty, unit) {
    return ltr(unit === null ? trimTrailingZeros(qty) : formatQtyWithUnit(qty, unit.precision, unit.symbol));
}
/** A percentage is N-5's rule, not N-4's — hence the trimmer. */
export function pct(value) {
    return ltr(`${trimTrailingZeros(value)}%`);
}
/** The "who" block under the header fields. */
export function party(label, name) {
    return html `
    <section class="doc-party">
      <div class="label">${label}</div>
      <div class="name">${name}</div>
    </section>
  `;
}
/** The free-text block at the foot, only when there is text to print. */
export function notesBlock(label, text) {
    if (text === null || text.trim() === '')
        return null;
    return html `<section class="doc-notes"><strong>${label}</strong><br />${text}</section>`;
}
/** The priced line table every money document draws. */
export function pricedLineTable(lines, currency, locale, print) {
    return html `
    <table class="doc-table">
      <thead>
        <tr>
          <th>${t('lineNo', locale)}</th>
          ${print.showItemCode ? html `<th>${t('itemCode', locale)}</th>` : null}
          <th>${t('description', locale)}</th>
          <th class="amount">${t('qty', locale)}</th>
          <th class="amount">${t('unitPrice', locale)}</th>
          ${print.showDiscountColumn ? html `<th class="amount">${t('discountPct', locale)}</th>` : null}
          <th class="amount">${t('lineTotal', locale)}</th>
        </tr>
      </thead>
      <tbody>
        ${lines.map((line) => html `
            <tr>
              <td>${ltr(String(line.lineNo))}</td>
              ${print.showItemCode ? html `<td>${line.itemCode === null ? null : ltr(line.itemCode)}</td>` : null}
              <td>${line.description}</td>
              <td class="amount">${qtyCell(line.qty, line.unit)}</td>
              <td class="amount">${money(line.unitPrice, currency)}</td>
              ${print.showDiscountColumn ? html `<td class="amount">${pct(line.discountPct)}</td>` : null}
              <td class="amount">${money(line.lineTotal, currency)}</td>
            </tr>
          `)}
      </tbody>
    </table>
  `;
}
/**
 * The totals rail: subtotal, discount, VAT (under `showTaxColumn`), grand
 * total. No paid/due rows — those are the invoice's, where a receipt can be
 * allocated; a quotation or an order has no balance to owe.
 */
export function totalsBlock(totals, locale, print) {
    const c = totals.currency;
    return html `
    <section class="doc-totals">
      <div class="row"><span>${t('subtotal', locale)}</span>${money(totals.subtotal, c)}</div>
      <div class="row">
        <span>${t('discount', locale)} ${pct(totals.discountPct)}</span>
        ${money(totals.discountAmount, c)}
      </div>
      ${print.showTaxColumn
        ? html `<div class="row">
              <span>${t('tax', locale)} ${pct(totals.taxPct)}</span>
              ${money(totals.taxAmount, c)}
            </div>`
        : null}
      <div class="row grand"><span>${t('grandTotal', locale)}</span>${money(totals.grandTotal, c)}</div>
    </section>
  `;
}
/** A batch / serial cell on a quantity line: blank when the item tracks neither. */
export function trackingCell(batchNo, serialNos) {
    const parts = [...(batchNo === null || batchNo === '' ? [] : [batchNo]), ...serialNos];
    return parts.length === 0 ? null : ltr(parts.join(', '));
}
//# sourceMappingURL=record-parts.js.map